import React, { PropsWithChildren } from "react";

import { IconButton } from "@mui/material";
import { useHistory, useLocation, useParams } from "react-router-dom";

import usePutHousehold from "@fartherfinance/frontend/api/Households/hooks/usePutHousehold";
import { HouseholdId } from "@fartherfinance/frontend/api/Households/types";

import { HouseholdProvider, useHouseholdContext } from "../context";
import { HouseholdFormWithMembers } from "../types";
import {
  formatEditHouseholdResponse,
  formatHouseholdDefaultFormFields,
} from "../utils";
import Arrow from "@src/assets/svg/components/Arrow";
import Drawer, {
  extraLargeDrawer,
} from "@src/multiCustodian/components/Drawer/Drawer";
import Button from "@src/multiCustodian/components/MUI/Button/Button";
import { useAdvisorPageInit } from "@src/multiCustodian/hooks/Advisor/useAdvisorPageInit";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import Households from "@src/multiCustodian/pages/Advisor/Households/Households";
import DrawerHero from "@src/sharedComponents/DrawerHero/DrawerHero";
import TitleBlock from "@src/sharedComponents/PageHero/TitleBlock";
import { handleApiError } from "@src/utils/handleApiError";

import HouseholdsDrawerTabs, { HouseholdTabPath } from "./HouseholdsDrawerTabs";

import styles from "./HouseholdsDrawer.module.css";
interface Props {
  tab?: HouseholdTabPath;
  footer?: React.ReactNode;
}

const HouseholdsDrawerContent = (props: PropsWithChildren<Props>) => {
  const { householdId, household, form } = useHouseholdContext();
  const { auth } = useAdvisorPageInit();

  const history = useHistory();

  const location = useLocation();

  const notify = useStatusNotification();

  const { mutateAsync: updateHousehold } = usePutHousehold(auth!);

  const closeDrawer = (): void => {
    history.push({ pathname: "/Advisor/Clients", search: location.search });
  };

  const householdDisplayName = household?.name || "";

  const handleOnSaveHousehold = async (formData: HouseholdFormWithMembers) => {
    if (!householdId) {
      return;
    }

    try {
      await updateHousehold({
        householdId,
        body: formatEditHouseholdResponse(formData),
      });
    } catch (error) {
      handleApiError({
        error: error,
        errorMessage: "Error updating household",
        genericErrorMessage: "Error updating household",
        notify,
      });
    }
  };

  const handleCancel = () => {
    form.reset(formatHouseholdDefaultFormFields(household));
    closeDrawer();
  };

  return (
    <Drawer
      isDrawerOpen
      onClose={closeDrawer}
      footer={props.footer}
      container_style={extraLargeDrawer}
    >
      <DrawerHero
        start={
          <>
            <IconButton onClick={closeDrawer}>
              <Arrow size={18} rotate="left" />
            </IconButton>

            <TitleBlock title={householdDisplayName} />
          </>
        }
        end={
          <>
            {props.tab === HouseholdTabPath.PROFILE ? (
              <div className={styles.buttonGroup}>
                <Button
                  text={"Cancel"}
                  variant="text"
                  buttonType="primary"
                  onClick={handleCancel}
                />
                <Button
                  text={"Save"}
                  variant="contained"
                  buttonType="secondary"
                  className={styles.formButton}
                  onClick={form.handleSubmit(handleOnSaveHousehold)}
                />
              </div>
            ) : null}
          </>
        }
      />
      <HouseholdsDrawerTabs>{props.children}</HouseholdsDrawerTabs>
    </Drawer>
  );
};

const HouseholdsDrawer = (props: PropsWithChildren<Props>) => {
  const { householdId, tab } = useParams<{
    householdId: HouseholdId;
    tab: HouseholdTabPath;
  }>();

  return (
    <Households>
      <HouseholdProvider householdId={householdId}>
        <HouseholdsDrawerContent {...props} tab={tab} />
      </HouseholdProvider>
    </Households>
  );
};

export default HouseholdsDrawer;
